import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './TypeWriter.css';
import './typewriter-responsive.css';

interface TypeWriterProps {
  words: string[];
  typingSpeed?: number;
  deletingSpeed?: number;
  delayBetween?: number;
  className?: string;
}

const TypeWriter: React.FC<TypeWriterProps> = ({
  words,
  typingSpeed = 100,
  deletingSpeed = 50,
  delayBetween = 1500,
  className = ''
}) => {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBlinking, setIsBlinking] = useState(true);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    const currentWord = words[currentWordIndex];

    // Type the word
    if (!isDeleting && displayedText !== currentWord) {
      timer = setTimeout(() => {
        setDisplayedText(currentWord.substring(0, displayedText.length + 1));
        setIsBlinking(false);
      }, typingSpeed);
    } 
    // Pause when word is fully typed
    else if (!isDeleting && displayedText === currentWord) {
      timer = setTimeout(() => {
        setIsDeleting(true);
        setIsBlinking(true);
      }, delayBetween);
    } 
    // Delete the word
    else if (isDeleting && displayedText !== '') {
      timer = setTimeout(() => {
        setDisplayedText(currentWord.substring(0, displayedText.length - 1));
        setIsBlinking(false);
      }, deletingSpeed);
    } 
    // Switch to next word
    else if (isDeleting && displayedText === '') {
      setIsDeleting(false);
      setIsBlinking(true);
      setCurrentWordIndex((prevIndex) => (prevIndex + 1) % words.length);
    }

    return () => clearTimeout(timer);
  }, [currentWordIndex, delayBetween, displayedText, isDeleting, deletingSpeed, typingSpeed, words]);

  return (
    <motion.div className={`typewriter-container ${className}`}>
      <span className="typewriter-text">{displayedText}</span>
      <AnimatePresence>
        {isBlinking && (
          <motion.span
            className="typewriter-cursor"
            initial={{ opacity: 1 }}
            animate={{ opacity: [1, 0, 1] }}
            exit={{ opacity: 0 }}
            transition={{ repeat: Infinity, duration: 0.8 }}
          >|</motion.span>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default TypeWriter;
